import { CheckCircledIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import {
  Avatar,
  Blockquote,
  Box,
  Button,
  Callout,
  Card,
  Flex,
  Strong,
  Text,
} from "@radix-ui/themes";
import { useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import {
  BOOKING_UNLOCK_GRACE_PERIOD_HOURS,
  BOOKING_UNLOCK_THRESHOLD_MINUTES,
  type Booking,
  type BookingUnlockInclude,
  type Unit,
  formatDate,
  formatTime,
  formatTimeUntil,
  isAtMostNHoursAfter,
  isInFuture,
  resolveBookingUnlockInclude,
} from "use-smart-locks-shared";
import UnlockBikeIcon from "../../assets/unlock-bike-icon.svg?react";
import { useBluetoothLock } from "../../shared/bluetooth/use-bluetooth-lock";
import { AppRedirectButtons } from "../../shared/components/app-redirect-buttons.component";
import { useGeolocation } from "../../shared/hooks";
import { useGenericErrorHandler } from "../../shared/hooks/use-generic-error-handler";
import { unlockLocksOfBookingResponseState } from "../../shared/state/booking-auth-data-map.state";
import { isWebPlatform } from "../../shared/utils/environment";
import { useUnlockLocksOfBookingMutation } from "../commands/use-unlock-locks-of-booking-mutation";
import { useCampingBooking } from "../hooks/use-camping-booking";
import { EndBooking } from "./camping-booking-unlock-page/end-booking.component";

export default function CampingBookingUnlockPage() {
  // When making changes to this component, please be aware that it needs to work in an offline scenario (at least after the booking has been started).
  // Apparently, the booking cannot be started without an internet connection.
  // But opening this page after the booking has been started, should work offline.
  // The main use case is to open bluetooth locks without an internet connection.
  const {
    booking,
    hasMultipleUnits,
    hasBluetoothLock,
    hasLock,
    hasEBike,
    requiresLocationVerification,
  } = useCampingBooking();

  // Shelly door + iLockit bikes: inferred when units have both a Shelly lock and a BLE lock.
  // We assume the Shelly side opens the station entrance, not key boxes.
  // In the future, we might want to add additional metadata to the locks to indicate what they open.
  // => Key box + bluetooth locks is unsupported for now.
  const hasStationDoorAndBluetoothLocks = hasBluetoothLock && hasLock;
  const includesUnitLocks = booking.units.every((unit) => !unit.pin);
  const [isEndBookingInProgress, setIsEndBookingInProgress] = useState(false);
  const [isOpenLocksInProgress, setIsOpenLocksInProgress] = useState(false);
  const [isStationDoorOpenSuccess, setIsStationDoorOpenSuccess] =
    useState(false);
  const [
    unlockLocksOfBookingResponseCache,
    setUnlockLocksOfBookingResponseCache,
  ] = useRecoilState(unlockLocksOfBookingResponseState(booking.id));

  const { mutateAsync: unlockAsync, status: unlockStatus } =
    useUnlockLocksOfBookingMutation({
      onSuccess(data, { payload }) {
        setUnlockLocksOfBookingResponseCache(data);
        if (resolveBookingUnlockInclude(payload?.include).includes("relays")) {
          setIsStationDoorOpenSuccess(true);
        }
      },
    });

  const { getLocationOrThrowError } = useGeolocation();
  const { handleError } = useGenericErrorHandler();

  const unlockWithLocation = async (include?: BookingUnlockInclude[]) => {
    const location =
      !booking.startedAt && requiresLocationVerification
        ? await getLocationOrThrowError()
        : undefined;
    return unlockAsync({
      bookingId: booking.id,
      payload: { ...location, include },
    });
  };

  const openLocks = async (include?: BookingUnlockInclude[]) => {
    setIsOpenLocksInProgress(true);
    try {
      await unlockWithLocation(include);
    } catch (error) {
      handleError(error, "Leider ist ein unerwarteter Fehler aufgetreten.");
    } finally {
      setIsOpenLocksInProgress(false);
    }
  };

  const {
    openLock,
    closeLock,
    updateDeviceAuthenticationData,
    readLockStatus,
    isLoading: isLockOperationInProgress,
  } = useBluetoothLock({ stationSlug: booking.stationSlug });

  const startBookingOrEnsureBluetoothAuthForUnit = async (unitId: string) => {
    let result = unlockLocksOfBookingResponseCache;

    if (!booking.startedAt) {
      // In case the booking has not been started yet:
      // - in pure bluetooth mode, unlocking all is the same as unlocking bluetooth only
      // - in bluetooth + relays mode, we unlock all locks because we assume the station hasn't been opened yet
      result = await unlockWithLocation();
    } else if (!result?.unitIdsToBluetoothAuthentication[unitId]) {
      // In case the booking has been started and we don't have authentication data for the unit yet, we unlock bluetooth only.
      // Unlocking all locks is not desired: after booking has started station door and bluetooth locks should be opened separately via dedicated buttons.
      result = await unlockWithLocation(["bluetooth"]);
    }

    const authenticationData = result.unitIdsToBluetoothAuthentication[unitId];
    if (!authenticationData) {
      const error =
        "Authentifizierungsdaten für Schloss nicht verfügbar. Bitte Service-Hotline kontaktieren.";
      toast.error(error);
      throw new Error(error);
    }

    updateDeviceAuthenticationData(
      authenticationData.deviceName,
      authenticationData,
    );

    return authenticationData;
  };

  const getLockStatus = async (unitId: string) => {
    const { deviceName } =
      await startBookingOrEnsureBluetoothAuthForUnit(unitId);
    return readLockStatus(deviceName);
  };

  const openBluetoothLock = async (
    unitId: string,
    unitType: Unit["unitType"],
  ) => {
    const { deviceName } =
      await startBookingOrEnsureBluetoothAuthForUnit(unitId);
    await openLock(deviceName, unitType);
  };

  const closeBluetoothLock = async (
    unitId: string,
    unitType: Unit["unitType"],
  ) => {
    const { deviceName } =
      await startBookingOrEnsureBluetoothAuthForUnit(unitId);
    await closeLock(deviceName, unitType);
  };

  const isUnlockAvailable = !isInFuture(
    booking.starts,
    BOOKING_UNLOCK_THRESHOLD_MINUTES,
  );
  const isUnlockLoading = unlockStatus === "pending";
  const isUnlockDisabled =
    !isUnlockAvailable ||
    isUnlockLoading ||
    isOpenLocksInProgress ||
    !!booking.endedAt;
  const isExpired = !isAtMostNHoursAfter(
    booking.ends,
    BOOKING_UNLOCK_GRACE_PERIOD_HOURS,
  );

  const isAnyLockOperationInProgress =
    isEndBookingInProgress ||
    isLockOperationInProgress ||
    isUnlockLoading ||
    isOpenLocksInProgress;
  const hasCompletedStationOpenStep = hasStationDoorAndBluetoothLocks
    ? Boolean(booking.startedAt)
    : true;
  const canOpenBluetoothLock =
    isUnlockAvailable && hasBluetoothLock && hasCompletedStationOpenStep;
  const canCloseBluetoothLock = Boolean(booking.startedAt) && hasBluetoothLock;

  const [, rerender] = useReducer((s) => !s, false);

  useEffect(() => {
    if (isUnlockAvailable) return;
    // TODO: is once a second really necessary? maybe speed it up once we reach 1min?
    const interval = setInterval(rerender, 1000);

    return () => clearInterval(interval);
  }, [isUnlockAvailable]);

  if (isExpired)
    return (
      <Flex direction="column" gap="4" minWidth="100%">
        <Callout.Root>
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            Ihre Buchung ist abgelaufen. Vielen Dank für die Nutzung unseres
            Services!
          </Callout.Text>
        </Callout.Root>
      </Flex>
    );

  if (hasBluetoothLock)
    return (
      <Flex direction="column" gap="4" minWidth="100%">
        <Flex justify="center">
          <UnlockBikeIcon className="unlock-bike-icon" aria-hidden />
        </Flex>
        {hasStationDoorAndBluetoothLocks && !booking.startedAt ? (
          <BookingPickupHeader
            booking={booking}
            hasMultipleUnits={hasMultipleUnits}
          />
        ) : (
          <>
            <Text>
              Sie können Ihr{hasMultipleUnits ? "e Fahrräder" : " Fahrrad"}{" "}
              <Strong>
                {formatTimeUntil(
                  booking.starts,
                  BOOKING_UNLOCK_THRESHOLD_MINUTES,
                )}
              </Strong>{" "}
              benutzen.
            </Text>
            <Blockquote>
              Gebucht für {formatDate(booking.starts)}, ab{" "}
              {formatTime(booking.starts)}
            </Blockquote>
          </>
        )}
        {isWebPlatform() ? (
          <AppRedirectButtons
            message="Diese Buchung enthält Bluetooth-Schlösser, die nur über die App geöffnet werden können. Bitte öffnen Sie diese Seite in der App bzw. laden Sie die App herunter."
            showOpenInAppButton={true}
            bookingCode={booking.slug}
          />
        ) : (
          <>
            {hasStationDoorAndBluetoothLocks && (
              <>
                <Button
                  disabled={isUnlockDisabled}
                  onClick={() => void openLocks(["relays"])}
                  loading={isOpenLocksInProgress}
                >
                  {booking.startedAt
                    ? "Eingangstür erneut öffnen"
                    : "Eingangstür öffnen"}
                </Button>
                {isStationDoorOpenSuccess && (
                  <Callout.Root color="green">
                    <Callout.Icon>
                      <CheckCircledIcon />
                    </Callout.Icon>
                    <Callout.Text>Eingangstür geöffnet.</Callout.Text>
                  </Callout.Root>
                )}
              </>
            )}
            {(!hasStationDoorAndBluetoothLocks || booking.startedAt) &&
              booking.units.map((unit) => (
                <UnitCard
                  key={unit.id}
                  unit={unit}
                  openLock={openBluetoothLock}
                  closeLock={closeBluetoothLock}
                  isUnlockAvailable={isUnlockAvailable}
                  isUnlockDisabled={isUnlockDisabled}
                  canOpenBluetoothLock={canOpenBluetoothLock}
                  canCloseBluetoothLock={canCloseBluetoothLock}
                  isLockOperationInProgress={isAnyLockOperationInProgress}
                />
              ))}
            <EndBooking
              hasEBike={hasEBike}
              getLockStatus={getLockStatus}
              isLockOperationInProgress={isAnyLockOperationInProgress}
              onEndBookingStateChange={setIsEndBookingInProgress}
            />
          </>
        )}
      </Flex>
    );

  return (
    <Flex direction="column" gap="4" minWidth="100%">
      <Flex justify="center">
        <UnlockBikeIcon className="unlock-bike-icon" aria-hidden />
      </Flex>

      <BookingPickupHeader
        booking={booking}
        hasMultipleUnits={hasMultipleUnits}
      />

      {(hasLock || includesUnitLocks) && (
        <Text>
          Elektronisch gesicherte Fahrräder und Türen können durch Klicken auf
          den unten stehenden Knopf geöffnet werden.
        </Text>
      )}

      {booking.units.some((unit) => unit.pin) && (
        <Text>
          Fahrräder mit Zahlenschloss können mit der PIN aufgeschlossen werden.
        </Text>
      )}

      {booking.units.map((unit) => (
        <UnitCard
          key={unit.id}
          unit={unit}
          isUnlockAvailable={isUnlockAvailable}
          isUnlockDisabled={isUnlockDisabled}
          openLock={openBluetoothLock}
          closeLock={closeBluetoothLock}
          canOpenBluetoothLock={canOpenBluetoothLock}
          canCloseBluetoothLock={canCloseBluetoothLock}
          isLockOperationInProgress={isAnyLockOperationInProgress}
        />
      ))}

      {hasLock && (
        <Button
          disabled={isUnlockDisabled}
          onClick={() => void openLocks()}
          loading={isOpenLocksInProgress}
        >
          Eingangstür {includesUnitLocks ? "und Schlüsselkästen" : ""} öffnen
        </Button>
      )}
      {isStationDoorOpenSuccess && (
        <Callout.Root color="green">
          <Callout.Icon>
            <CheckCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            Eingangstür {includesUnitLocks ? "und Schlüsselkästen" : ""}{" "}
            geöffnet, bitte nehmen Sie Ihr
            {hasMultipleUnits ? "e Fahrräder" : " Fahrrad"}.
          </Callout.Text>
        </Callout.Root>
      )}

      <EndBooking
        hasEBike={hasEBike}
        getLockStatus={getLockStatus}
        isLockOperationInProgress={isAnyLockOperationInProgress}
        onEndBookingStateChange={setIsEndBookingInProgress}
      />
    </Flex>
  );
}

function BookingPickupHeader({
  booking,
  hasMultipleUnits,
}: {
  booking: Pick<Booking, "starts" | "ends" | "pickupLocation">;
  hasMultipleUnits: boolean;
}) {
  return (
    <>
      <Text>
        Sie können Ihr{hasMultipleUnits ? "e Fahrräder" : " Fahrrad"}{" "}
        <Strong>
          {formatTimeUntil(booking.starts, BOOKING_UNLOCK_THRESHOLD_MINUTES)}
        </Strong>{" "}
        in der <Strong>{booking.pickupLocation}</Strong> abholen.
      </Text>
      <Blockquote>
        am {formatDate(booking.starts)}, von {formatTime(booking.starts)} bis{" "}
        {formatTime(booking.ends)}
      </Blockquote>
    </>
  );
}

function UnitCard({
  unit,
  isUnlockAvailable,
  isUnlockDisabled,
  openLock,
  closeLock,
  canOpenBluetoothLock,
  canCloseBluetoothLock,
  isLockOperationInProgress,
}: {
  unit: Unit;
  isUnlockAvailable: boolean;
  isUnlockDisabled: boolean;
  openLock: (unitId: string, unitType: Unit["unitType"]) => Promise<void>;
  closeLock: (unitId: string, unitType: Unit["unitType"]) => Promise<void>;
  canOpenBluetoothLock: boolean;
  canCloseBluetoothLock: boolean;
  isLockOperationInProgress: boolean;
}) {
  const { handleError } = useGenericErrorHandler();
  const [isOpening, setIsOpening] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleOpenLock = async () => {
    try {
      setIsOpening(true);
      await openLock(unit.id, unit.unitType);
    } catch (error) {
      handleError(error, "Schloss konnte nicht geöffnet werden");
    } finally {
      setIsOpening(false);
    }
  };

  const handleCloseLock = async () => {
    try {
      setIsClosing(true);
      await closeLock(unit.id, unit.unitType);
    } catch (error) {
      handleError(error, "Schloss konnte nicht geschlossen werden");
    } finally {
      setIsClosing(false);
    }
  };

  const disableBluetoothOpenButton =
    !canOpenBluetoothLock || isLockOperationInProgress || isUnlockDisabled;
  const disableBluetoothCloseButton =
    !canCloseBluetoothLock || isLockOperationInProgress || isUnlockDisabled;

  return (
    <Card size="1">
      <Flex gap="3" align="center">
        <Avatar
          className="unit"
          size="3"
          src={unit.imageUrl}
          radius="large"
          fallback={unit.name.charAt(0)}
        />
        <Box flexGrow="1">
          <Text as="div" size="2" weight="bold">
            {unit.name}
          </Text>
          <Text as="div" size="2" color="gray">
            {unit.description}
          </Text>
        </Box>

        <Box width="80px">
          <Flex direction="column" gap="0">
            {unit.pickupHint && <Text size="1">{unit.pickupHint}</Text>}
            {unit.pin && (
              <Text>PIN: {isUnlockAvailable ? unit.pin : "****"}</Text>
            )}
          </Flex>
        </Box>
      </Flex>
      {unit.hasBluetoothLock && (
        <Flex direction="column" mt="4" gap="4">
          <Callout.Root>
            <Callout.Text>
              <ol style={{ paddingLeft: "1rem" }}>
                {unit.unitType === "e-bike" && (
                  <li>Trennen Sie das Fahrrad vom Strom.</li>
                )}
                <li>Aktivieren Sie Bluetooth an Ihrem Handy.</li>
                <li>Klicken Sie auf &quot;Schloss öffnen&quot;.</li>
              </ol>
            </Callout.Text>
          </Callout.Root>
          <Flex gap="2">
            <Button
              style={{ flex: "1 1 0" }}
              onClick={() => void handleOpenLock()}
              disabled={disableBluetoothOpenButton}
              loading={isOpening}
            >
              Schloss öffnen
            </Button>
            <Button
              style={{ flex: "1 1 0" }}
              variant="soft"
              onClick={() => void handleCloseLock()}
              disabled={disableBluetoothCloseButton}
              loading={isClosing}
            >
              Schloss schließen
            </Button>
          </Flex>
        </Flex>
      )}
    </Card>
  );
}
